//================================================================
//  Page: Assign/Novate Agreement Form
//================================================================

//  Purpose: Form for assign/novate agreement

//================================================================

//Libraries
import React, { useState, useContext, useEffect, useReducer} from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import LookUpRHF from '../../../Components/LookUpRHF/LookUpRHF';
import UserSearchRHF from '../../../Components/UserSearchRHF/UserSearchRHF';
import SelectVendorField from '../../../Components/SelectVendorField/SelectVendorField';

//Functions
import WriteDocument from '../../../Library/WriteDocument';
import CreatePoANumber from '../../../Library/CreatePoANumber';
import UploadFile from '../../../Library/UploadFile';
import GetCollection from '../../../Library/GetCollection';

//Images
import FileIcon from '../../../Components/Images/Icon_File_Black.svg'

//CSS


export default function AssignNovateAgreement({
    setPageStatus,
    setFormSubmission,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State & useStates
    // --------------------------------------------------

    const { register, handleSubmit, setValue, reset, watch, clearErrors, setError, formState: { errors } } = useForm();

    const [ budgetOwners, setBudgetOwners] = useState([]);
    const [ financeManagers, setFinanceManagers] = useState([]);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store form data not saved in react hook forms
    const [formData, setFormData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'supportingDocs': [],
            'supportingDocsError': '',
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler 
    const onSubmit = (data) => {

        //------------------------------------------------------
        //  Validation Checks
        //------------------------------------------------------

        let preventSubmit = false;

        // Check User Selected for All Required User Search Fields
        //  - Sponsor Name
        if (data?.sponsornamegivenname === undefined || data?.sponsornamegivenname === '') {
            preventSubmit = true;
            setError('sponsorname', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (data.sponsornamesurname === undefined || data.sponsornamesurname === '') {
            preventSubmit = true;
            setError('sponsorname', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        // Check the Vendor Lookup is populated
        // - New Vendor > Check all details are there
        if (data.newvendorname?.length > 0 && (data.newvendortradingname === undefined || data.newvendortradingname === '')) {
            preventSubmit = true;
            setError('newvendorname', {
                type: 'required',
                message: 'This field is required.'
            });

            setError('newvendortradingname', {
                type: 'required',
                message: 'This field is required.'
            });
        }

        // Existing Vendor > Check all details are there
        if (data.vendorname?.length > 0 && (data['vendorname-vendorid'] === undefined || data['vendorname-vendorid'] === '')) {
            preventSubmit = true;
            setError('vendorname', {
                type: 'required',
                message: 'This field is required.'
            });
        }

        // Check if ALL validations have passed
        if (preventSubmit === true) return;

        //------------------------------------------------------
        //  Write Document
        //------------------------------------------------------
        
        setPageStatus('pending');

        // Generate the PoA Number 
        return CreatePoANumber()
        .then((poaNumber) => {

            const filePromises = [];

            // Check if attachment exists > Upload File
            formData.supportingDocs.forEach((document) => {

                filePromises.push(
                    UploadFile(`requests/${poaNumber[0]}/${document?.fileId}`, document?.fileObject)
                );

            })

            // ----------------------------------------------
            //  Settle Promises > Write to Firestore
            // ----------------------------------------------

            return Promise.all(filePromises)
            .then((urls) => {

                const updatedDocs = [];

                // Loop through docs > Add URL
                formData?.supportingDocs.forEach((document, index) => {
                    updatedDocs.push({
                    'fileUrl': urls[index],
                    'fileId': document?.fileId,
                    'fileName': document?.fileName,
                    'fileType': document?.fileType
                    })
                })

                // Find approvers
                const budgetOwnerDoc = budgetOwners.find(user => user.role === data?.budgetowner);
                const financeManagerDoc = financeManagers.find(user => user.role === data?.financemanager);

                // Create task
                const commercialTask = {
                    'parentrequestid': poaNumber[0],
                    'requestorigin': 'Digital',
                    'tasktype': 'Digital Commercial Request',
                    'taskname': 'Digital Commercial Request',
                    'taskdescription': data.shortdescription,
                    'applicationname': data?.applicationname,
                    'applicationid': data['applicationname-applicationid'] === undefined ? '' : data['applicationname-applicationid'],
                    'stage': 'Triage',
                    'status': 'Initialised',
                    'assignmentgroup': 'Commercial',
                    'requesttype': 'Assign/Novate Agreement',
                    'activityfeedvisible': true,
                    'approvalhubvisible': false,
                    'region': data?.region,
                    'assignedto': {
                        'email': 'Unassigned',
                        'givenname': '',
                        'surname': ''
                    },
                    'assigneddate': null,
                    'createddate': new Date(),
                    'createdby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'activityfeed': [
                        {
                            'activitydate': new Date(),
                            'actionedby': {
                                'email': getUser?.emailaddress,
                                'givenname': getUser?.givenname,
                                'surname': getUser?.surname
                            },
                            'action': 'created',
                            'comments': '',
                        }
                    ],
                    'urgency': data?.urgency, 
                    'impact': data?.impact,

                    // Commercial Fields
                    'requestername': getUser.emailaddress,
                    'supportingdocs': updatedDocs,
                    'shortdescription': data?.shortdescription,
                    'problemtosolve': data?.problemtosolve,
                    'expectedoutcome': data?.expectedoutcome,
                    'expectedimpact': data?.expectedimpact,
                    'commercialscope': data?.commercialscope,
                    'sponsorname': {
                        'email': data?.sponsorname,
                        'givenname': data?.sponsornamegivenname,
                        'surname': data?.sponsornamesurname
                    },
                    'buysellagreement': data?.buysellagreement,
                    'agreementtype': data?.agreementtype,
                    'lendleasecompany': data?.lendleasecompany,
                    'vendor': {
                        'vendorname': data?.vendorname === undefined ? '' : data?.vendorname,
                        'tradingas': data?.vendorname ? data['vendorname-tradingas'] : '',
                        'vendorid': data?.vendorname ? data['vendorname-vendorid'] : ''
                    },
                    'newvendorname': data?.newvendorname === undefined ? '' : data?.newvendorname,
                    'newvendorcontactphone': data?.newvendorphone === undefined ? '' : data?.newvendorphone,
                    'newvendorcontactemail': data?.newvendoremail === undefined ? '' : data?.newvendoremail,
                    'newvendortradingname': data?.newvendortradingname === undefined ? '' : data?.newvendortradingname,

                    // Get the full details of the budget owner and finance manager
                    'budgetowner': {
                        'email': budgetOwnerDoc?.approveremail,
                        'givenname': budgetOwnerDoc?.approvergivenname,
                        'surname': budgetOwnerDoc?.approversurname,
                        'approvalbucket': budgetOwnerDoc?.approvalbucket,
                        'role': budgetOwnerDoc?.role,
                    },
                    'financemanager': {
                        'email': financeManagerDoc?.approveremail,
                        'givenname': financeManagerDoc?.approvergivenname,
                        'surname': financeManagerDoc?.approversurname,
                        'approvalbucket': financeManagerDoc?.approvalbucket,
                        'role': financeManagerDoc?.role,
                    },
                    'currency': '',
                    'estannualcontractvalue': '',
                    'esttotalcontractvalue': '',
                    'projectcode': '',
                    'tasknumber': '',
                    'expendituretype': '',
                    'secondroundapprovals': [],
                    'signedcontracts': [],
                    'agreement': {
                        'vendor': {
                            'vendorname': '',
                            'vendorid': ''
                        },
                        'agreementstartdate': null,
                        'agreementenddate': null,
                        'currency': '',
                        'totalagreementamount': '',
                        'autorenewals': '',
                        'noticeperiodforautorenewaltermination': {},
                        'extensionagreement': '',
                        'noticeperiodforextension': {},
                        'executiondate': null,
                        'hardsavings': '0',
                        'softsavings': '0'
                    },
                    'poraised': true, // By default, this is true
                    'potask': {
                        'shortdescription': `Raise PO for ${data?.applicationname}`,
                        'commercialscope': `Raise PO for ${data?.applicationname}`
                    },
                    'comments': []

                }

                return WriteDocument('requests', poaNumber[0], commercialTask, true)
                .then(() => {

                    // Reset form and show success message
                    reset();
                    setFormData({
                        'supportingDocs': []
                    });
                    setFormSubmission(commercialTask);
                    setPageStatus('success');

                })

            })

        })
        .catch((error) => {

            console.log(error)
            setPageStatus('error-fatal');

        })

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // onload
    // - Fetch the Digital Commerical Approvers
    useEffect(() => {

        GetCollection('approvers').then((approvers) => {

            // Find all 'Budget Owner (LoA) Approval' docs
            setBudgetOwners(
                approvers.filter((approver) => approver?.approvalbucket === 'Budget Owner (LoA) Approval')
            );

            // Find all 'Finance Manager Approval' docs
            setFinanceManagers(
                approvers.filter((approver) => approver?.approvalbucket === 'Finance Manager Approval')
            );

        }).catch((error) => {

            console.log('Unable to find Approvers!', error);

        });

    }, []);

    // Set all the default values on the form
    useEffect(() => {

        if (getUser === undefined) return;

        setValue('requestername', getUser.emailaddress);

    // eslint-disable-next-line
    }, [getUser])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='h-full flex flex-col gap-2'>

            {/* ============================ */}
            {/*          Questions           */}
            {/* ============================ */}

            <form className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>

                <div>

                    {/* ------------- REQUEST INFORMATION ------------- */}

                    <h5 className='text-[18px] text-teal-600 mt-2 mb-1'>Request Information</h5>

                    {/* =========================================================== */}
                    {/*  1. Application Name                                        */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 1. Application Name </label>
                        <p className='italic font-light'>
                            Fill in this field, if you would like to link an application to this agreement. To view all applications, click <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory?view=applications` : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? 'https://uatinventory.digitalservices.lendlease.com/inventory?view=applications' : 'https://inventory.digitalservices.lendlease.com/inventory?view=applications'} target='_blank' rel='noopener noreferrer'>here</a>.
                        </p>                        
                        <LookUpRHF
                            fieldId={'applicationname'}
                            collectionId={'applications'}
                            dataField={'applicationname'}
                            dataFieldSecondary={'vendor.vendorname'}
                            dataFieldTertiary={'applicationid'}
                            placeholder={'Search application'}
                            required={false}
                            register={register}
                            setValue={setValue}
                            errors={errors.applicationname}
                            clearErrors={clearErrors}
                            watch={watch}
                            setError={setError}
                        ></LookUpRHF>
                    </div>

                    {/* =========================================================== */}
                    {/*  2. Short Description                                       */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 2. Short Description <span className='text-[#C4314B]'>*</span></label>
                        <textarea
                            className={errors.shortdescription ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                            {...register('shortdescription', {
                            required: 'This field is required'
                            })}
                            placeholder='Enter a description'
                            autoComplete='off'
                        ></textarea>

                        {errors.shortdescription && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.shortdescription?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  3. What is the problem you are trying to solve?            */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 3. What is the problem you are trying to solve? <span className='text-[#C4314B]'>*</span></label>
                        <textarea
                            className={errors.problemtosolve ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                            {...register('problemtosolve', {
                            required: 'This field is required'
                            })}
                            placeholder='Describe the problem'
                            autoComplete='off'
                        ></textarea>

                        {errors.problemtosolve && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.problemtosolve?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  4. What is the outcome you are looking for?                */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 4. What is the outcome you are looking for? <span className='text-[#C4314B]'>*</span></label>
                        <textarea
                            className={errors.expectedoutcome ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                            {...register('expectedoutcome', {
                            required: 'This field is required'
                            })}
                            placeholder='Describe what success would look like'
                            autoComplete='off'
                        ></textarea>

                        {errors.expectedoutcome && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.expectedoutcome?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  5. What would be the impact if this didn't go ahead?       */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 5. What would be the impact if this didn't go ahead? <span className='text-[#C4314B]'>*</span></label>
                        <textarea
                            className={errors.expectedimpact ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                            {...register('expectedimpact', {
                            required: 'This field is required'
                            })}
                            placeholder='Describe what would happen and any Business impact if this request was not approved'
                            autoComplete='off'
                        ></textarea>

                        {errors.expectedimpact && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.expectedimpact?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  6. Requester Name                                          */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 6. Requester Name <span className='text-[#C4314B]'>*</span></label>
                        <div className='md:w-[400px]'>
                            <LookUpRHF
                                fieldId={'requestername'}
                                collectionId={'users'}
                                dataField={'emailaddress'}
                                placeholder={'Search user by email address'}
                                required={false}
                                register={register}
                                setValue={setValue}
                                errors={errors.itowner}
                                additionalQueries={['usertype', '==', 'lendleaseuser']}
                                clearErrors={clearErrors}
                                disabled={true}
                                setError={setError}
                            ></LookUpRHF>
                        </div>
                    </div>

                    {/* =========================================================== */}
                    {/*  7. Region                                                  */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 7. Region <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.region ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('region', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='AMER'> AMER </option>
                            <option value='ASIA'> ASIA </option>
                            <option value='AUST'> AUST </option>
                            <option value='EUR'> EUR </option>
                            <option value='Global'> Global </option>
                        </select>

                        {errors.region && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.region?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  8. Sponsor Name                                            */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 8. Sponsor Name <span className='text-[#C4314B]'>*</span></label>
                        <div className='md:w-[400px]'>
                            <UserSearchRHF
                                dataField={'sponsorname'}
                                required={true}
                                register={register}
                                setValue={setValue}
                                clearErrors={clearErrors}
                                setError={setError}
                                errors={errors.sponsorname}
                            ></UserSearchRHF>
                        </div>
                        
                        {errors.sponsorname && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.sponsorname?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  9. Supporting Documents                                    */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 9. Supporting Documents </label>

                        {/* File Input */}
                        <div className='Attach-File-Onload-Container'>
                            <input
                                className={formData?.supportingDocsError?.length === 0 ? ('Input-Field-Text') : ('Input-Field-Text-Error')}
                                style={{ padding: '0px' }}
                                type='file'
                                accept='.pdf,.doc,.txt,.xlsx,.docx,.csv,.png,.jpg'
                                onChange={(event) => {

                                    const file = event.target.files[0];

                                    if (file.size > 20000000) {
                                        setFormData({
                                            'supportingDocsError': 'File size too large. Please upload a file smaller than 20MB.'
                                        });
                                        return;
                                    }

                                    //Set File Id
                                    const fileType = file?.name?.split('.')[1];
                                    const fileId = `${Date.now().toString()}${Math.floor(Math.random() * (99999 - 10000 + 1) + 10000)}.${fileType}`;

                                    // Validate File Types
                                    const allowedFileTypes = ['pdf', 'doc', 'txt', 'xlsx', 'docx', 'csv', 'png', 'jpg'];

                                    if (allowedFileTypes.includes(fileType.toLowerCase()) === false) {
                                        return setFormData({ 'supportingDocsError': `Please upload a file in the following format(s): ${allowedFileTypes.join(', ')}.` });
                                    }

                                    setFormData({
                                    'supportingDocsError': '',
                                    'supportingDocs': [
                                        ...formData.supportingDocs,
                                        {
                                        'fileId': fileId,
                                        'fileName': file.name,
                                        'fileObject': file,
                                        'fileType': fileType,
                                        }
                                    ]
                                    });

                                    // Clear File Input
                                    event.target.value = '';

                                }}
                            ></input>
                        </div>

                        {/* Uploads */}
                        <div className='w-full flex flex-col rounded-md border border-[#D8D8D8]' hidden={formData.supportingDocs.length > 0 ? false : true}>
                            {
                            formData?.supportingDocs?.map((file, index) => (
                                <div key={index} className='flex flex-row justify-between items-center border-b border-b-[#D8D8D8] last:border-0 px-3 py-1'>

                                {/* File Name */}
                                <div className='flex flex-row gap-2 items-center p-1'>
                                    <img src={FileIcon} alt='File'></img>
                                    {file?.fileName}
                                </div>

                                {/* Delete Button */}
                                <div 
                                    className='Cancel-Icon cursor-pointer mt-1' 
                                    onClick={() => {
                                        setFormData({ 'supportingDocs': formData.supportingDocs.filter(item => item !== file) });
                                    }
                                }></div>

                                </div>
                            ))
                            }
                        </div>

                        {/* Error Message */}
                        <label className='font-medium text-[#DE0000]' hidden={formData?.supportingDocsError.length === 0}>{formData?.supportingDocsError}</label>

                    </div>

                    {/* ------------- CONTRACT INFORMATION ------------- */}

                    <h5 className='text-[18px] text-teal-600 mt-4 mb-2'>Contract Information</h5>

                    {/* =========================================================== */}
                    {/*  10. Detailed Request / Scope Description                   */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 10. Detailed Request / Scope Description <span className='text-[#C4314B]'>*</span></label>
                        <textarea
                            className={errors.commercialscope ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                            {...register('commercialscope', {
                            required: 'This field is required'
                            })}
                            placeholder='Describe the scope of this request'
                            autoComplete='off'
                        ></textarea>

                        {errors.commercialscope && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.commercialscope?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  11. Urgency                                                */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 11. Urgency <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.urgency ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('urgency', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='Low'> Low </option>
                            <option value='Medium'> Medium </option>
                            <option value='High'> High </option>
                        </select>

                        {errors.urgency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.urgency?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  12. Impact                                                 */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 12. Impact <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.impact ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('impact', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='Low'> Low </option>
                            <option value='Medium'> Medium </option>
                            <option value='High'> High </option>
                        </select>

                        {errors.impact && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.impact?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  13. Buy/Sell Agreement                                     */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 13. Is this a Buy or Sell Agreement? <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.buysellagreement ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('buysellagreement', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='Buy'> Buy </option>
                            <option value='Sell'> Sell </option>
                            <option value='N/A'> N/A </option>
                        </select>

                        {errors.buysellagreement && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.buysellagreement?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  14. Agreement Type                                         */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 14. Agreement Type <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.agreementtype ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('agreementtype', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='Consultancy Services Agreement (CSA)'> Consultancy Services Agreement (CSA) </option>
                            <option value='Hardware Order'> Hardware Order </option>
                            <option value='Letter of Intent (LoI)'> Letter of Intent (LoI) </option>
                            <option value='Master Services Agreement (MSA)'> Master Services Agreement (MSA) </option>
                            <option value='Non-Disclosure Agreement (NDA)'> Non-Disclosure Agreement (NDA) </option>
                            <option value='Novation/Assignment Agreement'> Novation/Assignment Agreement </option>
                            <option value='Service Order (SO)'> Service Order (SO) </option>
                            <option value='Software License'> Software License </option>
                            <option value='Subscription'> Subscription </option>
                            <option value='Supply Agreement'> Supply Agreement </option>
                            <option value='Variation'> Variation </option>
                        </select>

                        {errors.agreementtype && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.agreementtype?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  15. Lendlease Company                                      */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 15. Lendlease Company (Signing Entity) <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.lendleasecompany ? 'Input-Field-Select-Error md:w-[300px]' : 'Input-Field-Select md:w-[300px]'}
                            {...register('lendleasecompany', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            <option value='TBC'>TBC</option>
                            <option value='Lendlease Asia Holdings Pte. Ltd'>Lendlease Asia Holdings Pte. Ltd</option>
                            <option value='Lendlease Building Pty Ltd'>Lendlease Building Pty Ltd</option>
                            <option value='Lendlease Chilled Water (Barangaroo South) Pty Limited'>Lendlease Chilled Water (Barangaroo South) Pty Limited</option>
                            <option value='Lendlease Construction Pty Limited'>Lendlease Construction Pty Limited</option>
                            <option value='Lendlease Design Build'>Lendlease Design Build</option>
                            <option value='Lendlease Development Pty Ltd'>Lendlease Development Pty Ltd</option>
                            <option value='Lendlease Digital Asia Pte Ltd'>Lendlease Digital Asia Pte Ltd</option>
                            <option value='Lendlease Digital Australia Holdings Pty Limited'>Lendlease Digital Australia Holdings Pty Limited</option>
                            <option value='Lendlease Digital Australia Pty Ltd'>Lendlease Digital Australia Pty Ltd</option>
                            <option value='Lendlease Digital Investments Pty Limited'>Lendlease Digital Investments Pty Limited</option>
                            <option value='Lendlease Digital US Holdings LLC'>Lendlease Digital US Holdings LLC</option>
                            <option value='Lendlease Digital US LLC'>Lendlease Digital US LLC</option>
                            <option value='Lendlease Embedded Network (Barangaroo South) Pty Limited'>Lendlease Embedded Network (Barangaroo South) Pty Limited</option>
                            <option value='Lendlease Engineering Pty Ltd'>Lendlease Engineering Pty Ltd</option>
                            <option value='Lendlease Europe Limited'>Lendlease Europe Limited</option>
                            <option value='Lendlease Global Commercial Trust Management Pte Ltd'>Lendlease Global Commercial Trust Management Pte Ltd</option>
                            <option value='Lendlease Group Services Pty Limited'>Lendlease Group Services Pty Limited</option>
                            <option value='Lendlease ICT Services, Inc'>Lendlease ICT Services, Inc</option>
                            <option value='Lendlease Management Services Limited'>Lendlease Management Services Limited</option>
                            <option value='Lendlease Project Management & Construction (Shanghai) Co Ltd'>Lendlease Project Management & Construction (Shanghai) Co Ltd</option>
                            <option value='Lendlease Recycled Water (Barangaroo South) Pty Limited'>Lendlease Recycled Water (Barangaroo South) Pty Limited</option>
                            <option value='Lendlease Retirement Living Holding Pty Limited'>Lendlease Retirement Living Holding Pty Limited</option>
                            <option value='Lendlease Services Pty Limited'>Lendlease Services Pty Limited</option>
                            <option value='Lendlease Singapore Pte Ltd'>Lendlease Singapore Pte Ltd</option>
                            <option value='Lendlease Technology Delivery Pty Limited'>Lendlease Technology Delivery Pty Limited</option>
                            <option value='Lendlease Technology Pty Ltd'>Lendlease Technology Pty Ltd</option>
                            <option value='Podium Asset Holdings Pty Limited'>Podium Asset Holdings Pty Limited</option>
                            <option value='Podium Property Services LLC'>Podium Property Services LLC</option>
                            <option value='Lendlease (Elephant & Castle) Ltd'>Lendlease (Elephant & Castle) Ltd</option>
                            <option value='Lendlease (US) Construction Inc'>Lendlease (US) Construction Inc</option>
                            <option value='Lendlease (US) Construction, LMB Inc'>Lendlease (US) Construction, LMB Inc</option>
                            <option value='Lendlease (US) Development'>Lendlease (US) Development</option>
                            <option value='Lendlease (US) Partnerships'>Lendlease (US) Partnerships</option>
                            <option value='Lendlease (US) Services Inc'>Lendlease (US) Services Inc</option>
                        </select>
                        {errors.lendleasecompany && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.lendleasecompany?.message}</p>}
                    </div>

                    {/* ------------- OTHER PARTY INFORMATION (VENDOR/CUSTOMER) ------------- */}

                    <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Other Party Information (Vendor / Customer) </h5>

                    {/* =========================================================== */}
                    {/*  16. Vendor Name                                            */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 16. Vendor Name <span className='text-[#C4314B]'>*</span></label>
                        <SelectVendorField
                            register={register}
                            setValue={setValue}
                            errors={errors}
                            clearErrors={clearErrors}
                            setError={setError}
                        ></SelectVendorField>
                    </div>

                    {/* ------------- FINANCIAL INFORMATION ------------- */}

                    <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Financial Information </h5>

                    {/* =========================================================== */}
                    {/*  17. Budget Owner                                           */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 17. Budget Owner <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.budgetowner ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('budgetowner', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                            {
                                budgetOwners.map((user, index) => (
                                    <option key={index} value={user.role}>{user.approvergivenname} {user.approversurname}</option>
                                ))
                            }
                        </select>

                        {errors.budgetowner && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.budgetowner?.message}</p>}
                    </div>

                    {/* =========================================================== */}
                    {/*  18. Finance Manager                                        */}
                    {/* =========================================================== */}

                    <div className='Form-Row'>
                        <label className='font-medium'> 18. Finance Manager <span className='text-[#C4314B]'>*</span></label>
                        <select
                            className={errors.financemanager ? ('Input-Field-Select-Error md:w-[300px]') : ('Input-Field-Select md:w-[300px]')}
                            {...register('financemanager', {
                            required: 'This field is required'
                            })}
                        >
                            <option hidden value=''>-</option>
                                                        {
                                financeManagers.map((user, index) => (
                                    <option key={index} value={user.role}>{user.approvergivenname} {user.approversurname}</option>
                                ))
                            }
                        </select>

                        {errors.financemanager && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.financemanager?.message}</p>}
                    </div>

                </div>

                {/* ===================================== */}
                {/*  Submit Buttons                       */}
                {/* ===================================== */}

                <div>
                    <button style={{ marginTop: '30px' }} className='Primary-Button max-md:w-full' type='submit' value='submit'>Submit Request</button>
                </div>

            </form>

        </div>
    )
}